// Imports for external libraries go here.
import React, { FC, useState, useRef, useEffect, useContext, useCallback } from 'react';
import clsx from 'clsx';
import { useLazyQuery } from '@apollo/client';
import { Icon, Text, Types } from '@marriott/mi-ui-library';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { phoenixAccountGetGiftCardTransactions } from '../../modules/graph';
import { useGiftCardStore } from '../../modules/store/giftCardStore';
import { PageContext } from '../../modules/context/PageContext';
import { generateApolloClientHeaders } from '../../modules/utils';
import { PROFILE_CONSTANTS, convertDateToLocaleSpecific } from '../../modules';
import { SkeletonLoaderV2 } from '../SkeletonLoaderV2';
import { TransactionHistoryProps, TransactionHistoryItem, CardTransactionHistory } from './TransactionHistory.types';
import { StyledTransactionHistory } from './TransactionHistory.styles';
import { transactionHistoryMock } from './__mock__/TransactionHistoryMock';
import { formatGiftCardCurrency } from '../GiftCard/__helpers__';

// Use named rather than default exports.
export const TransactionHistory: FC<TransactionHistoryProps> = props => {
  const isAuthorMode = props?.isAuthorMode;
  const { IS_LOCAL_DEV } = process.env;
  const pageContext = useContext(PageContext);
  const { currentLocale } = pageContext || {};
  const dataLoaded = useRef<boolean>(false);
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistoryItem[]>();
  const { giftCardISOnumber, selectedGiftCardSerialNumber } = useGiftCardStore(state => state);
  const selectedGiftCard = props?.selectedGiftCard;
  const isGiftCardReportLost = selectedGiftCard?.cardStatus === 'ON_HOLD' && selectedGiftCard?.hasHoldNotLost === false;
  const [hasUXLError, setHasUXLError] = useState<boolean>(false);
  const [transactionCurrency, setTransactionCurrency] = useState<string | undefined>();
  const [getGiftCardTransactions, { loading: isTransactionHistoryLoading }] = useLazyQuery(
    phoenixAccountGetGiftCardTransactions
  );

  useEffect(() => {
    if (isAuthorMode) {
      setTransactionHistory(
        transactionHistoryMock?.commerce?.payment?.giftcard?.certificateTransactionHistory?.transactionHistory?.edges
      );
      setTransactionCurrency(
        transactionHistoryMock?.commerce?.payment?.giftcard?.certificateTransactionHistory?.currency
      );
    }
  }, []);

  useEffect(() => {
    const shouldFetchTransactions =
      !props.isAuthorMode && props?.modalId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID?.VIEW_GIFT_CARDS;

    if (shouldFetchTransactions) {
      fetchGiftCardTransactions();
    }
  }, [props.modalId]);

  // Helper function to handle successful completion of gift card transactions query
  const handleTransactionQueryCompleted = useCallback((data: CardTransactionHistory) => {
    dataLoaded.current = true;

    // Extract the certificate transaction history for easier access
    const certificateTransactionHistory = data?.commerce?.payment?.giftcard?.certificateTransactionHistory;

    // Check for UXL errors in the response
    const hasUXLErrors = certificateTransactionHistory?.errors && certificateTransactionHistory.errors.length > 0;

    if (hasUXLErrors) {
      setHasUXLError(true);
      setTransactionHistory([]);
      setTransactionCurrency(undefined);
      return;
    }

    const transactionEdges = certificateTransactionHistory?.transactionHistory?.edges;

    if (transactionEdges && transactionEdges.length > 0) {
      setTransactionHistory(transactionEdges);
      setTransactionCurrency(certificateTransactionHistory?.currency);
      setHasUXLError(false);
    } else {
      setTransactionHistory([]);
      setTransactionCurrency(undefined);
      setHasUXLError(false);
    }
  }, []);

  const handleTransactionQueryError = useCallback(() => {
    dataLoaded.current = true;
    setTransactionHistory([]);
    setTransactionCurrency(undefined);
    setHasUXLError(true);
  }, []);

  const fetchGiftCardTransactions = useCallback(() => {
    getGiftCardTransactions({
      variables: {
        input: {
          cardInformation: {
            cardISO: giftCardISOnumber,
            endDate: null,
            offset: 0,
            serialNumber: selectedGiftCardSerialNumber,
            showCurrency: null,
            startDate: null,
          },
        },
      },
      context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
      fetchPolicy: 'no-cache',
      onCompleted: handleTransactionQueryCompleted,
      onError: handleTransactionQueryError,
    });
  }, [
    getGiftCardTransactions,
    handleTransactionQueryCompleted,
    handleTransactionQueryError,
    giftCardISOnumber,
    selectedGiftCardSerialNumber,
    IS_LOCAL_DEV,
    pageContext,
  ]);

  return (
    <StyledTransactionHistory
      data-testid="account-TransactionHistory"
      data-component-name="m-account-TransactionHistory"
      className="view-gift-card__transaction-history p-3"
    >
      <div className="view-gift-card__transaction-history-header d-flex align-items-center pb-3">
        <Icon
          iconClass={clsx('view-gift-card__transaction-history-icon icon-decorative', props?.transactionHistoryIcon)}
        />
        <div className="t-subtitle-m pl-2">{props?.transactionHistoryLabel}</div>
      </div>
      <div
        className={clsx(
          'view-gift-card__transaction-history-content d-flex flex-column',
          hasUXLError || isGiftCardReportLost ? 'pr-2' : 'pr-3 pb-3'
        )}
        tabIndex={0}
        role="region"
        aria-label={props?.transactionHistoryLabel}
      >
        {hasUXLError && (
          <div className="d-flex px-3 pt-4 pb-2 justify-content-start align-items-start">
            <Icon iconClass="icon-warning view-gift-card__transaction-history-warning-icon" />
            <Text
              copyText={props?.transactionHistoryErrorMessage}
              fontSize={Types.size.small}
              element={Types.tags.span}
              customClass="t-font-s pl-3"
            />
          </div>
        )}
        {isTransactionHistoryLoading ? (
          <SkeletonLoaderV2
            className="view-gift-card__transaction-history-loader mt-3"
            data-testid="account-SkeletonLoaderV2"
          />
        ) : isGiftCardReportLost ? (
          <div className="d-flex px-3 pt-4 pb-2 justify-content-start align-items-start">
            <Icon iconClass="icon-information view-gift-card__transaction-history-info-icon" />
            <Text
              copyText={props?.transactionHistoryReportCardStatus}
              fontSize={Types.size.small}
              element={Types.tags.span}
              customClass="t-font-s pl-3"
            />
          </div>
        ) : (
          transactionHistory?.map((item: TransactionHistoryItem, index: number) => {
            const isLastItem = index === transactionHistory?.length - 1;
            const { symbol, value, currencyCode } = formatGiftCardCurrency(
              Math.abs(parseFloat(item?.node?.amount ?? '0')),
              transactionCurrency ?? props?.cardCurrency,
              currentLocale
            );
            return (
              <div
                className={clsx('d-flex flex-column py-3', {
                  'view-gift-card__transaction-history-item-border': !isLastItem,
                })}
                key={index}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <Text
                    fontSize={Types.size.small}
                    element={Types.tags.div}
                    copyText={convertDateToLocaleSpecific(item?.node?.transactionLocalDate || '', currentLocale) || ''}
                    customClass="t-font-s"
                  />
                  <Text
                    fontSize={Types.size.small}
                    element={Types.tags.div}
                    copyText={`${item?.node?.amount?.startsWith('-') ? '-' : '+'}${symbol}${value} ${currencyCode}`}
                    customClass="t-font-s"
                  />
                </div>
                <div className="t-subtitle-m pt-3">{item?.node?.storeName}</div>
              </div>
            );
          })
        )}
      </div>
    </StyledTransactionHistory>
  );
};
